import { freeTextSchema, identifierSchema, isinSchema, micSchema, personNameSchema, postalCodeSchema, TEXT_MAX_LENGTH } from '@1trading/fast-api-domain/forms';
import { z } from 'zod';

import { BINARY_ANSWERS } from '@/constants/answers';
import {
  ACCOUNT_PURPOSES,
  ACTIVE_ENTITY_CRITERIA,
  ACTIVITY_BANDS,
  AccountPurpose,
  BeneficialOwnerType,
  BUSINESS_SIZE_BANDS,
  BUSINESS_SUB_TYPES,
  BUSINESS_SUB_TYPES_BY_BUSINESS_TYPE,
  BUSINESS_TYPES,
  type BusinessSubType,
  BusinessType,
  CONTROLLING_PERSON_TYPES,
  EDD_CATEGORIES,
  EddCategory,
  EMPLOYEE_BANDS,
  EntityClassification,
  ExcludedPersonType,
  FundsOwnership,
  LEGAL_FORMS,
  LegalForm,
  LicenceCopySource,
  MANAGEMENT_ROLES,
  ManagementRole,
  NoTinReason,
  OWNERSHIP_TYPES,
  PROHIBITED_CATEGORIES,
  ProhibitedCategory,
  RegulatedMarketListing,
  SCREENING_FREQUENCIES,
  SOURCES_OF_FUNDS,
  SourceOfFunds,
  TRADING_VOLUME_BANDS,
} from '@/constants/institution';
import i18n from '@/lib/i18n';
import { getCountryIsEu, getCountryPostalCodeUse, mainstreamCountrySchema } from '@/services/api/mainstream';
import { identifierErrors, nameErrors, postalCodeErrors, textErrors } from '@/utils/error-messages';
import { createPhoneNumberSchema } from '@/utils/phone-number';
import { webUrlSchema } from '@/utils/url';
import {
  institutionDetailsAuthorisationSchema,
  institutionDetailsBranchOperationsSchema,
  institutionDetailsClientCategorisationSchema,
  institutionDetailsCompanySchema,
  institutionDetailsDecisionMakerSchema,
  institutionDetailsExecutionSchema,
  institutionDetailsIncorporationSchema,
  institutionDetailsRegisteredAddressSchema,
  institutionDetailsRegulatoryInformationSchema,
  institutionDetailsReportingSchema,
} from './institution-details';

const hasUniquePartyIds = (parties: Array<{ partyId: string }>) => new Set(parties.map(({ partyId }) => partyId)).size === parties.length;

type InstitutionDetailsV2TypeOfBusinessAnswers = { typeOfBusiness: BusinessType; subTypeOfBusiness?: BusinessSubType; typeOfBusinessOther?: string };

type InstitutionDetailsV2LegalFormAnswers = { legalForm: LegalForm; legalFormOther?: string };

const hasRequiredSubTypeOfBusiness = ({ typeOfBusiness, subTypeOfBusiness }: InstitutionDetailsV2TypeOfBusinessAnswers) =>
  !BUSINESS_SUB_TYPES_BY_BUSINESS_TYPE[typeOfBusiness].length || subTypeOfBusiness !== undefined;

const isSubTypeOfTypeOfBusiness = ({ typeOfBusiness, subTypeOfBusiness }: InstitutionDetailsV2TypeOfBusinessAnswers) =>
  subTypeOfBusiness === undefined || BUSINESS_SUB_TYPES_BY_BUSINESS_TYPE[typeOfBusiness].includes(subTypeOfBusiness);

const isTypeOfBusinessOtherDescribed = ({ typeOfBusiness, typeOfBusinessOther }: InstitutionDetailsV2TypeOfBusinessAnswers) =>
  typeOfBusiness !== BusinessType.OTHER || !!typeOfBusinessOther;

const isLegalFormOtherDescribed = ({ legalForm, legalFormOther }: InstitutionDetailsV2LegalFormAnswers) => legalForm !== LegalForm.OTHER || !!legalFormOther;

export const withInstitutionDetailsV2TypeOfBusinessRefinements = <Answers extends z.ZodType<InstitutionDetailsV2TypeOfBusinessAnswers>>(answers: Answers) =>
  answers
    .refine(hasRequiredSubTypeOfBusiness, { path: ['subTypeOfBusiness'], error: i18n.t('institutionDetails.company.errors.subTypeOfBusiness') })
    .refine(isSubTypeOfTypeOfBusiness, { path: ['subTypeOfBusiness'], error: i18n.t('institutionDetails.company.errors.subTypeOfBusiness') })
    .refine(isTypeOfBusinessOtherDescribed, { path: ['typeOfBusinessOther'], error: i18n.t('institutionDetails.company.errors.typeOfBusinessOther') });

export const withInstitutionDetailsV2LegalFormRefinements = <Answers extends z.ZodType<InstitutionDetailsV2LegalFormAnswers>>(answers: Answers) =>
  answers.refine(isLegalFormOtherDescribed, { path: ['legalFormOther'], error: i18n.t('institutionDetails.company.errors.legalFormOther') });

export const institutionDetailsV2CompanySchema = withInstitutionDetailsV2LegalFormRefinements(
  withInstitutionDetailsV2TypeOfBusinessRefinements(
    institutionDetailsCompanySchema.extend({
      registeredAddress: institutionDetailsRegisteredAddressSchema,
      typeOfBusiness: z.enum(BUSINESS_TYPES, { error: i18n.t('institutionDetails.company.errors.typeOfBusiness') }),
      registrationNumber: identifierSchema({ errors: identifierErrors('institutionDetails.company.errors.registrationNumber') }),
      dateOfIncorporation: z.iso.date(i18n.t('institutionDetails.company.errors.dateOfIncorporation')),
      website: webUrlSchema(i18n.t('institutionDetails.company.errors.website')).optional(),
      subTypeOfBusiness: z.enum(BUSINESS_SUB_TYPES, { error: i18n.t('institutionDetails.company.errors.subTypeOfBusiness') }).optional(),
      typeOfBusinessOther: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('institutionDetails.company.errors.typeOfBusinessOther') }).optional(),
      legalForm: z.enum(LEGAL_FORMS, { error: i18n.t('institutionDetails.company.errors.legalForm') }),
      legalFormOther: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('institutionDetails.company.errors.legalFormOther') }).optional(),
    }),
  ),
);

export type InstitutionDetailsV2Company = z.infer<typeof institutionDetailsV2CompanySchema>;

export const institutionDetailsV2BusinessProfileClientsSchema = z.object({
  totalClients: z.int({ error: i18n.t('institutionDetails.businessProfile.errors.totalClients') }).min(1, i18n.t('institutionDetails.businessProfile.errors.totalClients')),
  clientTypeBreakdown: z
    .object({
      naturalPersonsPct: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
      legalPersonsPct: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
    })
    .refine((breakdown) => breakdown.naturalPersonsPct + breakdown.legalPersonsPct === 100, {
      error: i18n.t('institutionDetails.businessProfile.errors.breakdownTotal'),
      when: ({ issues }) => issues.length === 0,
    }),
  clientGeographyBreakdown: z
    .object({
      eea: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
      uk: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
      restOfEurope: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
      americas: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
      apac: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
      middleEastAfrica: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
    })
    .refine((breakdown) => breakdown.eea + breakdown.uk + breakdown.restOfEurope + breakdown.americas + breakdown.apac + breakdown.middleEastAfrica === 100, {
      error: i18n.t('institutionDetails.businessProfile.errors.breakdownTotal'),
      when: ({ issues }) => issues.length === 0,
    }),
  clientRiskBreakdown: z
    .object({
      low: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
      medium: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
      high: z
        .int({ error: i18n.t('institutionDetails.businessProfile.errors.percentage') })
        .min(0, i18n.t('institutionDetails.businessProfile.errors.percentage'))
        .max(100, i18n.t('institutionDetails.businessProfile.errors.percentage')),
    })
    .refine((breakdown) => breakdown.low + breakdown.medium + breakdown.high === 100, {
      error: i18n.t('institutionDetails.businessProfile.errors.breakdownTotal'),
      when: ({ issues }) => issues.length === 0,
    }),
});

export type InstitutionDetailsV2BusinessProfileClients = z.infer<typeof institutionDetailsV2BusinessProfileClientsSchema>;

export const institutionDetailsV2BusinessProfileBaseSchema = z.object({
  totalAssets: z.enum(BUSINESS_SIZE_BANDS, { error: i18n.t('institutionDetails.businessProfile.errors.totalAssets') }),
  annualRevenue: z.enum(BUSINESS_SIZE_BANDS, { error: i18n.t('institutionDetails.businessProfile.errors.annualRevenue') }),
  employees: z.enum(EMPLOYEE_BANDS, { error: i18n.t('institutionDetails.businessProfile.errors.employees') }),
  topExchanges: z
    .array(freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('institutionDetails.businessProfile.errors.topExchange') }))
    .max(5, i18n.t('institutionDetails.businessProfile.errors.topExchangesTooMany')),
});

export const institutionDetailsV2BusinessProfileSchema = institutionDetailsV2BusinessProfileBaseSchema.and(
  z.discriminatedUnion(
    'fundsOwnership',
    [
      z.object({
        fundsOwnership: z.literal(FundsOwnership.OWN),
      }),
      institutionDetailsV2BusinessProfileClientsSchema.extend({
        fundsOwnership: z.enum([FundsOwnership.CUSTOMER, FundsOwnership.BOTH]),
      }),
    ],
    {
      error: i18n.t('institutionDetails.businessProfile.errors.fundsOwnership'),
    },
  ),
);

export type InstitutionDetailsV2BusinessProfile = z.infer<typeof institutionDetailsV2BusinessProfileSchema>;

export const institutionDetailsV2AddressSchema = z
  .object({
    addressLines: z
      .array(freeTextSchema({ maxLength: TEXT_MAX_LENGTH.SHORT, errors: textErrors('institutionDetails.address.errors.addressLine') }))
      .min(1, i18n.t('institutionDetails.address.errors.addressLine'))
      .max(2),
    locality: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.SHORT, errors: textErrors('institutionDetails.address.errors.locality') }),
    postalCode: postalCodeSchema({ errors: postalCodeErrors('institutionDetails.address.errors.postalCodeInvalid') }),
    country: mainstreamCountrySchema(i18n.t('institutionDetails.address.errors.country')),
  })
  .superRefine(async (address, context) => {
    if (address.postalCode.trim() !== '') return;

    if ((await getCountryPostalCodeUse(address.country)) === 'required') {
      context.addIssue({ code: 'custom', message: i18n.t('institutionDetails.address.errors.postalCode'), path: ['postalCode'] });
    }
  });

export type InstitutionDetailsV2Address = z.infer<typeof institutionDetailsV2AddressSchema>;

const institutionDetailsV2EntityTaxResidencyNoTinJurisdictionSchema = mainstreamCountrySchema(i18n.t('institutionDetails.taxResidency.errors.jurisdiction')).refine(
  async (country) => !(await getCountryIsEu(country)),
  { error: i18n.t('institutionDetails.taxResidency.errors.tinRequiredForEuResidence') },
);

export const institutionDetailsV2EntityTinSchema = identifierSchema({ errors: identifierErrors('institutionDetails.taxResidency.errors.tin') });

export const institutionDetailsV2NoTinExplanationSchema = freeTextSchema({
  maxLength: TEXT_MAX_LENGTH.LONG,
  multiline: true,
  errors: textErrors('institutionDetails.taxResidency.errors.noTinExplanation'),
});

// The entity's own residences; people use institutionDetailsV2TaxResidencesSchema, as retail identities do
// NL implementation (Besluit art. 3(2)): the no-TIN exception covers non-EU residences only
export const institutionDetailsV2EntityTaxResidencySchema = z.discriminatedUnion(
  'noTinReason',
  [
    z.object({
      jurisdiction: mainstreamCountrySchema(i18n.t('institutionDetails.taxResidency.errors.jurisdiction')),
      noTinReason: z.undefined(),
      tin: institutionDetailsV2EntityTinSchema,
    }),
    z.object({
      jurisdiction: institutionDetailsV2EntityTaxResidencyNoTinJurisdictionSchema,
      noTinReason: z.enum([NoTinReason.NOT_ISSUED, NoTinReason.NOT_REQUIRED]),
      tin: z.undefined(),
    }),
    z.object({
      jurisdiction: institutionDetailsV2EntityTaxResidencyNoTinJurisdictionSchema,
      noTinReason: z.literal(NoTinReason.UNABLE_TO_OBTAIN),
      tin: z.undefined(),
      noTinExplanation: institutionDetailsV2NoTinExplanationSchema,
    }),
  ],
  {
    error: i18n.t('institutionDetails.taxResidency.errors.noTinReason'),
  },
);

export type InstitutionDetailsV2EntityTaxResidency = z.infer<typeof institutionDetailsV2EntityTaxResidencySchema>;

const institutionDetailsV2TaxResidencesSchema = z
  .array(
    z.object({
      country: mainstreamCountrySchema(i18n.t('taxResidency.errors.country', { context: 'individual' })),
      taxId: z.string().min(1).optional(),
    }),
  )
  .min(1)
  .max(5)
  .refine((taxResidences) => new Set(taxResidences.map(({ country }) => country)).size === taxResidences.length, {
    error: i18n.t('taxResidency.errors.countryDuplicate'),
  });

// The same partyId marks the same person or entity wherever it is copied into another section
const institutionDetailsV2PersonSchema = z.object({
  partyId: z.uuid(),
  firstName: personNameSchema({ errors: nameErrors('institutionDetails.person.errors.firstName') }),
  lastName: personNameSchema({ errors: nameErrors('institutionDetails.person.errors.lastName') }),
  dateOfBirth: z.iso.date(i18n.t('institutionDetails.person.errors.dateOfBirth')),
  countryOfBirth: mainstreamCountrySchema(i18n.t('institutionDetails.person.errors.countryOfBirth')),
  localityOfBirth: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.SHORT, errors: textErrors('institutionDetails.person.errors.localityOfBirth') }),
  address: institutionDetailsV2AddressSchema,
});

export const institutionDetailsV2ContactablePersonSchema = institutionDetailsV2PersonSchema.extend({
  nationality: mainstreamCountrySchema(i18n.t('institutionDetails.person.errors.nationality')),
  email: z.email(i18n.t('institutionDetails.person.errors.email')),
  phoneNumber: createPhoneNumberSchema({
    areaCode: i18n.t('institutionDetails.person.errors.areaCode'),
    mobilePhone: i18n.t('institutionDetails.person.errors.mobilePhone'),
  }),
  taxResidences: institutionDetailsV2TaxResidencesSchema,
});

export type InstitutionDetailsV2ContactablePerson = z.infer<typeof institutionDetailsV2ContactablePersonSchema>;

export const institutionDetailsV2BeneficialOwnerSchema = z.discriminatedUnion(
  'type',
  [
    institutionDetailsV2ContactablePersonSchema.extend({
      type: z.literal(BeneficialOwnerType.OWNER),
      ownershipPct: z
        .number({ error: i18n.t('institutionDetails.ownership.errors.ownershipPct') })
        .min(20, i18n.t('institutionDetails.ownership.errors.ownershipPctBelowThreshold'))
        .max(100, i18n.t('institutionDetails.ownership.errors.ownershipPct')),
      isPep: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.ownership.errors.isPep') }),
    }),
    institutionDetailsV2ContactablePersonSchema.extend({
      type: z.literal(BeneficialOwnerType.SENIOR_MANAGING_OFFICIAL),
      isPep: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.ownership.errors.isPep') }),
    }),
  ],
  {
    error: i18n.t('institutionDetails.ownership.errors.uboType'),
  },
);

export type InstitutionDetailsV2BeneficialOwner = z.infer<typeof institutionDetailsV2BeneficialOwnerSchema>;

export const institutionDetailsV2CorporateOwnerBaseSchema = z.object({
  partyId: z.uuid(),
  companyName: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('institutionDetails.corporateOwner.errors.companyName') }),
  registrationNumber: identifierSchema({ errors: identifierErrors('institutionDetails.corporateOwner.errors.registrationNumber') }),
  dateOfIncorporation: z.iso.date(i18n.t('institutionDetails.corporateOwner.errors.dateOfIncorporation')),
  countryOfIncorporation: mainstreamCountrySchema(i18n.t('institutionDetails.corporateOwner.errors.countryOfIncorporation')),
  registeredAddress: institutionDetailsV2AddressSchema,
  taxId: identifierSchema({ errors: identifierErrors('institutionDetails.corporateOwner.errors.taxId') }),
  leiNumber: z
    .string()
    .regex(/^[a-zA-Z0-9]{20}$/, i18n.t('institutionDetails.corporateOwner.errors.leiNumber'))
    .optional(),
  website: webUrlSchema(i18n.t('institutionDetails.corporateOwner.errors.website')).optional(),
  ownershipPct: z
    .number({ error: i18n.t('institutionDetails.corporateOwner.errors.ownershipPct') })
    .min(20, i18n.t('institutionDetails.corporateOwner.errors.ownershipPctBelowThreshold'))
    .max(100, i18n.t('institutionDetails.corporateOwner.errors.ownershipPct')),
});

export const institutionDetailsV2CorporateOwnerTypeOfBusinessOtherSchema = freeTextSchema({
  maxLength: TEXT_MAX_LENGTH.MEDIUM,
  errors: textErrors('institutionDetails.corporateOwner.errors.typeOfBusinessOther'),
});

const institutionDetailsV2CorporateOwnerSchema = z.discriminatedUnion(
  'typeOfBusiness',
  [
    institutionDetailsV2CorporateOwnerBaseSchema.extend({
      typeOfBusiness: z.enum(
        BUSINESS_TYPES.filter((businessType) => businessType !== BusinessType.OTHER),
        { error: i18n.t('institutionDetails.corporateOwner.errors.typeOfBusiness') },
      ),
    }),
    institutionDetailsV2CorporateOwnerBaseSchema.extend({
      typeOfBusiness: z.literal(BusinessType.OTHER),
      typeOfBusinessOther: institutionDetailsV2CorporateOwnerTypeOfBusinessOtherSchema,
    }),
  ],
  {
    error: i18n.t('institutionDetails.corporateOwner.errors.typeOfBusiness'),
  },
);

export type InstitutionDetailsV2CorporateOwner = z.infer<typeof institutionDetailsV2CorporateOwnerSchema>;

export const hasManagementRole = (role: ManagementRole) => (management: Array<{ roles: ManagementRole[] }>) => management.some(({ roles }) => roles.includes(role));

export const institutionDetailsV2ManagementRolesSchema = z
  .array(z.enum(MANAGEMENT_ROLES), { error: i18n.t('institutionDetails.ownership.errors.managementRoles') })
  .min(1, i18n.t('institutionDetails.ownership.errors.managementRoles'))
  .refine((roles) => new Set(roles).size === roles.length, { error: i18n.t('institutionDetails.ownership.errors.managementRoles') });

export const institutionDetailsV2ManagementPersonSchema = institutionDetailsV2ContactablePersonSchema.extend({
  roles: institutionDetailsV2ManagementRolesSchema,
  isPep: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.ownership.errors.isPep') }),
});

export type InstitutionDetailsV2ManagementPerson = z.infer<typeof institutionDetailsV2ManagementPersonSchema>;

export const createInstitutionDetailsV2ManagementSchema = <Person extends z.ZodType<{ partyId: string; roles: ManagementRole[] }>>(personSchema: Person) =>
  z
    .array(personSchema, { error: i18n.t('institutionDetails.ownership.errors.management') })
    .min(1, i18n.t('institutionDetails.ownership.errors.management'))
    .max(20, i18n.t('institutionDetails.ownership.errors.managementTooMany'))
    .refine(hasUniquePartyIds, { error: i18n.t('institutionDetails.ownership.errors.duplicateParty') })
    .refine(hasManagementRole(ManagementRole.DIRECTOR), { error: i18n.t('institutionDetails.ownership.errors.managementDirectorRequired') });

const institutionDetailsV2ManagementSchema = createInstitutionDetailsV2ManagementSchema(institutionDetailsV2ManagementPersonSchema);

// With no UBOs to name, a listed company must name its senior managing official
const institutionDetailsV2ListedManagementSchema = institutionDetailsV2ManagementSchema.refine(hasManagementRole(ManagementRole.SENIOR_MANAGING_OFFICIAL), {
  error: i18n.t('institutionDetails.ownership.errors.managementSeniorManagingOfficialRequired'),
});

export const institutionDetailsV2ListedCompanySchema = z.object({
  mic: micSchema(i18n.t('institutionDetails.ownership.errors.mic')).optional(),
  ticker: identifierSchema({ errors: identifierErrors('institutionDetails.ownership.errors.ticker') })
    .transform((ticker) => ticker.toUpperCase())
    .optional(),
  isin: isinSchema(i18n.t('institutionDetails.ownership.errors.isin')).optional(),
});

/** The ISIN or the ticker finds the listed company, where the market alone doesn't */
export const hasListedCompanyIdentifier = ({ ticker, isin }: { ticker?: string; isin?: string }) => Boolean(ticker || isin);

const LISTED_COMPANY_IDENTIFIER_REQUIRED = { error: i18n.t('institutionDetails.ownership.errors.isinOrTicker') };

export const institutionDetailsV2ListedCompanyNameSchema = freeTextSchema({
  maxLength: TEXT_MAX_LENGTH.MEDIUM,
  errors: textErrors('institutionDetails.ownership.errors.listedCompanyName'),
});

const institutionDetailsV2OwnershipChartSchema = z.uuid(i18n.t('institutionDetails.ownership.errors.ownershipChart'));

export const institutionDetailsV2UboOwnershipBaseSchema = z.object({
  regulatedMarketListing: z.literal(RegulatedMarketListing.NONE),
  ownershipType: z.enum(OWNERSHIP_TYPES, { error: i18n.t('institutionDetails.ownership.errors.ownershipType') }),
  ownershipChart: institutionDetailsV2OwnershipChartSchema,
  bearerSharesPct: z
    .number({ error: i18n.t('institutionDetails.ownership.errors.bearerSharesPct') })
    .min(0, i18n.t('institutionDetails.ownership.errors.bearerSharesPct'))
    .max(100, i18n.t('institutionDetails.ownership.errors.bearerSharesPct')),
  ubos: z
    .array(institutionDetailsV2BeneficialOwnerSchema, { error: i18n.t('institutionDetails.ownership.errors.ubos') })
    .min(1, i18n.t('institutionDetails.ownership.errors.ubos'))
    .refine(hasUniquePartyIds, { error: i18n.t('institutionDetails.ownership.errors.duplicateParty') }),
  management: institutionDetailsV2ManagementSchema,
});

// Corporate owners don't replace natural-person UBOs: a person holding through a corporate owner is listed with their effective share
const institutionDetailsV2UboOwnershipSchema = z
  .discriminatedUnion(
    'hasCorporateOwners',
    [
      institutionDetailsV2UboOwnershipBaseSchema.extend({
        hasCorporateOwners: z.literal('NO'),
      }),
      institutionDetailsV2UboOwnershipBaseSchema.extend({
        hasCorporateOwners: z.literal('YES'),
        corporateOwners: z
          .array(institutionDetailsV2CorporateOwnerSchema, { error: i18n.t('institutionDetails.ownership.errors.corporateOwners') })
          .min(1, i18n.t('institutionDetails.ownership.errors.corporateOwners'))
          .refine(hasUniquePartyIds, { error: i18n.t('institutionDetails.ownership.errors.duplicateParty') })
          .refine((corporateOwners) => corporateOwners.reduce((total, corporateOwner) => total + corporateOwner.ownershipPct, 0) <= 100, {
            error: i18n.t('institutionDetails.ownership.errors.ownershipPctTotal'),
          }),
      }),
    ],
    {
      error: i18n.t('institutionDetails.ownership.errors.hasCorporateOwners'),
    },
  )
  .superRefine(({ ubos }, context) => {
    const owners = ubos.filter((ubo) => ubo.type === BeneficialOwnerType.OWNER);
    const hasSeniorManagingOfficial = ubos.some((ubo) => ubo.type === BeneficialOwnerType.SENIOR_MANAGING_OFFICIAL);

    if (owners.length && hasSeniorManagingOfficial) {
      context.addIssue({ code: 'custom', path: ['ubos'], message: i18n.t('institutionDetails.ownership.errors.seniorManagingOfficialWithOwners') });
    }

    if (owners.reduce((total, owner) => total + owner.ownershipPct, 0) > 100) {
      context.addIssue({ code: 'custom', path: ['ubos'], message: i18n.t('institutionDetails.ownership.errors.ownershipPctTotal') });
    }
  });

export const institutionDetailsV2OwnershipSchema = z.discriminatedUnion(
  'regulatedMarketListing',
  [
    z.object({
      regulatedMarketListing: z.literal(RegulatedMarketListing.LISTED),
      listedCompany: institutionDetailsV2ListedCompanySchema.refine(hasListedCompanyIdentifier, LISTED_COMPANY_IDENTIFIER_REQUIRED),
      management: institutionDetailsV2ListedManagementSchema,
    }),
    z.object({
      regulatedMarketListing: z.literal(RegulatedMarketListing.WHOLLY_OWNED_SUBSIDIARY),
      listedCompany: institutionDetailsV2ListedCompanySchema
        .extend({ name: institutionDetailsV2ListedCompanyNameSchema })
        .refine(hasListedCompanyIdentifier, LISTED_COMPANY_IDENTIFIER_REQUIRED),
      // Shows the chain of holdings up to the listed company
      ownershipChart: institutionDetailsV2OwnershipChartSchema,
      management: institutionDetailsV2ListedManagementSchema,
    }),
    institutionDetailsV2UboOwnershipSchema,
  ],
  {
    error: i18n.t('institutionDetails.ownership.errors.regulatedMarketListing'),
  },
);

export type InstitutionDetailsV2Ownership = z.infer<typeof institutionDetailsV2OwnershipSchema>;

const institutionDetailsV2RegulatoryInformationSupervisedSchema = institutionDetailsRegulatoryInformationSchema.extend({
  hasRegulatorySupervisionOrOversight: z.literal('YES'),
  isMifidInvestmentFirm: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.isMifidInvestmentFirm') }),
});

export const institutionDetailsV2ActiveJurisdictionsSchema = z
  .array(
    z.object({
      country: mainstreamCountrySchema(i18n.t('institutionDetails.regulatoryInformation.errors.activeJurisdictionCountry')),
      licensedEntities: z
        .int({ error: i18n.t('institutionDetails.regulatoryInformation.errors.activeJurisdictionLicensedEntities') })
        .min(0, i18n.t('institutionDetails.regulatoryInformation.errors.activeJurisdictionLicensedEntities')),
    }),
    { error: i18n.t('institutionDetails.regulatoryInformation.errors.activeJurisdictions') },
  )
  .min(1, i18n.t('institutionDetails.regulatoryInformation.errors.activeJurisdictions'))
  .refine((jurisdictions) => new Set(jurisdictions.map(({ country }) => country)).size === jurisdictions.length, {
    error: i18n.t('institutionDetails.regulatoryInformation.errors.activeJurisdictionsDuplicate'),
  });

export const institutionDetailsV2ApplicableAmlLawsSchema = freeTextSchema({
  maxLength: TEXT_MAX_LENGTH.LONG,
  multiline: true,
  errors: textErrors('institutionDetails.regulatoryInformation.errors.applicableAmlLaws'),
});

export const institutionDetailsV2OffshoreLicenceBranchesSchema = freeTextSchema({
  maxLength: TEXT_MAX_LENGTH.LONG,
  multiline: true,
  errors: textErrors('institutionDetails.regulatoryInformation.errors.offshoreLicenceBranches'),
});

export const institutionDetailsV2FinesOrCensureDetailsSchema = freeTextSchema({
  maxLength: TEXT_MAX_LENGTH.DETAILED,
  multiline: true,
  errors: textErrors('institutionDetails.regulatoryInformation.errors.finesOrCensureDetails'),
});

export const institutionDetailsV2RegulatoryInformationSchema = z
  .discriminatedUnion(
    'hasRegulatorySupervisionOrOversight',
    [
      z.object({
        hasRegulatorySupervisionOrOversight: z.literal('NO'),
      }),
      z.discriminatedUnion(
        'licenceCopySource',
        [
          institutionDetailsV2RegulatoryInformationSupervisedSchema.extend({
            licenceCopySource: z.literal(LicenceCopySource.DOCUMENT),
            licenceDocument: z.uuid(i18n.t('institutionDetails.regulatoryInformation.errors.licenceDocument')),
          }),
          institutionDetailsV2RegulatoryInformationSupervisedSchema.extend({
            licenceCopySource: z.literal(LicenceCopySource.URL),
            licenceUrl: webUrlSchema(i18n.t('institutionDetails.regulatoryInformation.errors.licenceUrl')),
          }),
        ],
        {
          error: i18n.t('institutionDetails.regulatoryInformation.errors.licenceCopySource'),
        },
      ),
    ],
    {
      error: i18n.t('institutionDetails.regulatoryInformation.errors.hasRegulatorySupervisionOrOversight'),
    },
  )
  .and(
    z.discriminatedUnion(
      'amlEquivalentLegislation',
      [
        z.object({
          amlEquivalentLegislation: z.literal('NO'),
        }),
        z.object({
          amlEquivalentLegislation: z.literal('YES'),
          applicableAmlLaws: institutionDetailsV2ApplicableAmlLawsSchema,
        }),
      ],
      {
        error: i18n.t('institutionDetails.regulatoryInformation.errors.amlEquivalentLegislation'),
      },
    ),
  )
  .and(
    z.discriminatedUnion(
      'offshoreLicence',
      [
        z.object({
          offshoreLicence: z.literal('NO'),
        }),
        z.object({
          offshoreLicence: z.literal('YES'),
          offshoreLicenceBranches: institutionDetailsV2OffshoreLicenceBranchesSchema,
        }),
      ],
      {
        error: i18n.t('institutionDetails.regulatoryInformation.errors.offshoreLicence'),
      },
    ),
  )
  .and(
    z
      .object({
        activeJurisdictions: institutionDetailsV2ActiveJurisdictionsSchema,
        underInvestigation: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.underInvestigation') }),
        individualsInvestigatedOrCensured: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.individualsInvestigatedOrCensured') }),
        findingsAgainstFirmOrIndividuals: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.findingsAgainstFirmOrIndividuals') }),
        impendingProceedings: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.impendingProceedings') }),
        finesOrCensureDetails: institutionDetailsV2FinesOrCensureDetailsSchema.optional(),
      })
      .superRefine((answers, context) => {
        const hasAdverseHistory = [
          answers.underInvestigation,
          answers.individualsInvestigatedOrCensured,
          answers.findingsAgainstFirmOrIndividuals,
          answers.impendingProceedings,
        ].includes('YES');

        if (hasAdverseHistory && !answers.finesOrCensureDetails) {
          context.addIssue({ code: 'custom', path: ['finesOrCensureDetails'], message: i18n.t('institutionDetails.regulatoryInformation.errors.finesOrCensureDetails') });
        }
      }),
  );

export type InstitutionDetailsV2RegulatoryInformation = z.infer<typeof institutionDetailsV2RegulatoryInformationSchema>;

export const institutionDetailsV2AmlCtfSanctionsAnswersSchema = z.object({
  screensMajorSanctionsLists: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  assessesPepRisk: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  screensSanctionsPepAdverseMedia: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  hasEscalationProcess: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  hasSuspiciousActivityPolicies: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  transactionMonitoringMethods: freeTextSchema({
    maxLength: TEXT_MAX_LENGTH.DETAILED,
    multiline: true,
    errors: textErrors('institutionDetails.amlCtfSanctions.errors.transactionMonitoringMethods'),
  }),
  hasStrReportingObligation: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  hasEmployeeEscalationProcess: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  prohibitsAnonymousAccounts: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  prohibitsUnlicensedBankAccounts: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  prohibitsServicesToUnlicensedBanks: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  prohibitsShellBanks: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  prohibitsServicesToShellBanks: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  policiesApprovedAnnually: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  recordRetentionCompliant: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  independentAmlAudit: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.amlCtfSanctions.errors.answer') }),
  itAndSoftwareProviders: freeTextSchema({
    maxLength: TEXT_MAX_LENGTH.DETAILED,
    multiline: true,
    errors: textErrors('institutionDetails.amlCtfSanctions.errors.itAndSoftwareProviders'),
  }),
});

export const institutionDetailsV2AmlCtfPolicyDocumentSchema = z.uuid(i18n.t('institutionDetails.amlCtfSanctions.errors.amlCtfPolicyDocument'));

export const institutionDetailsV2SanctionsPolicyDocumentSchema = z.uuid(i18n.t('institutionDetails.amlCtfSanctions.errors.sanctionsPolicyDocument'));

export const institutionDetailsV2AmlCtfSanctionsSchema = z.discriminatedUnion(
  'amlCtfPolicies',
  [
    z.discriminatedUnion(
      'sanctionsPolicies',
      [
        institutionDetailsV2AmlCtfSanctionsAnswersSchema.extend({
          amlCtfPolicies: z.literal('NO'),
          sanctionsPolicies: z.literal('NO'),
        }),
        institutionDetailsV2AmlCtfSanctionsAnswersSchema.extend({
          amlCtfPolicies: z.literal('NO'),
          sanctionsPolicies: z.literal('YES'),
          sanctionsPolicyDocument: institutionDetailsV2SanctionsPolicyDocumentSchema,
        }),
      ],
      {
        error: i18n.t('institutionDetails.amlCtfSanctions.errors.sanctionsPolicies'),
      },
    ),
    z.discriminatedUnion(
      'sanctionsPolicies',
      [
        institutionDetailsV2AmlCtfSanctionsAnswersSchema.extend({
          amlCtfPolicies: z.literal('YES'),
          amlCtfPolicyDocument: institutionDetailsV2AmlCtfPolicyDocumentSchema,
          sanctionsPolicies: z.literal('NO'),
        }),
        institutionDetailsV2AmlCtfSanctionsAnswersSchema.extend({
          amlCtfPolicies: z.literal('YES'),
          amlCtfPolicyDocument: institutionDetailsV2AmlCtfPolicyDocumentSchema,
          sanctionsPolicies: z.literal('YES'),
          sanctionsPolicyDocument: institutionDetailsV2SanctionsPolicyDocumentSchema,
        }),
      ],
      {
        error: i18n.t('institutionDetails.amlCtfSanctions.errors.sanctionsPolicies'),
      },
    ),
  ],
  {
    error: i18n.t('institutionDetails.amlCtfSanctions.errors.amlCtfPolicies'),
  },
);

export type InstitutionDetailsV2AmlCtfSanctions = z.infer<typeof institutionDetailsV2AmlCtfSanctionsSchema>;

export const institutionDetailsV2ComplianceTrainingSchema = z.object({
  trainingBoard: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.complianceTraining.errors.answer') }),
  trainingFirstLine: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.complianceTraining.errors.answer') }),
  trainingSecondLine: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.complianceTraining.errors.answer') }),
  trainingThirdLine: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.complianceTraining.errors.answer') }),
  complianceHeadcount: z
    .int({ error: i18n.t('institutionDetails.complianceTraining.errors.complianceHeadcount') })
    .min(0, i18n.t('institutionDetails.complianceTraining.errors.complianceHeadcount')),
  abcPolicies: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.complianceTraining.errors.answer') }),
  privacyPolicyCompliant: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.complianceTraining.errors.answer') }),
});

export type InstitutionDetailsV2ComplianceTraining = z.infer<typeof institutionDetailsV2ComplianceTrainingSchema>;

export const institutionDetailsV2KycCddAnswersSchema = z.object({
  verifiesCustomerIdentity: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.kycCdd.errors.answer') }),
  knowsExpectedActivity: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.kycCdd.errors.answer') }),
  retainsOwnershipDocuments: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.kycCdd.errors.answer') }),
  verifiesNatureAndPurpose: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.kycCdd.errors.answer') }),
  verifiesSourceOfFunds: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.kycCdd.errors.answer') }),
  identifiesUbos: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.kycCdd.errors.answer') }),
  verifiesRelatedParties: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.kycCdd.errors.answer') }),
  assignsRiskClassification: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.kycCdd.errors.answer') }),
  riskBasedPepScreening: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.kycCdd.errors.answer') }),
  hasKycReviewProcess: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.kycCdd.errors.answer') }),
  eddCategories: z
    .array(z.enum(EDD_CATEGORIES), { error: i18n.t('institutionDetails.kycCdd.errors.eddCategories') })
    .min(1, i18n.t('institutionDetails.kycCdd.errors.eddCategories')),
  eddCategoriesOther: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('institutionDetails.kycCdd.errors.eddCategoriesOther') }).optional(),
  prohibitedCategories: z
    .array(z.enum(PROHIBITED_CATEGORIES), { error: i18n.t('institutionDetails.kycCdd.errors.prohibitedCategories') })
    .min(1, i18n.t('institutionDetails.kycCdd.errors.prohibitedCategories')),
  prohibitedCategoriesOther: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('institutionDetails.kycCdd.errors.prohibitedCategoriesOther') }).optional(),
});

export const institutionDetailsV2CddPolicyDocumentSchema = z.uuid(i18n.t('institutionDetails.kycCdd.errors.cddPolicyDocument'));

export const institutionDetailsV2ScreeningFrequencySchema = z.enum(SCREENING_FREQUENCIES, { error: i18n.t('institutionDetails.kycCdd.errors.screeningFrequency') });

export const institutionDetailsV2KycCddSchema = z
  .discriminatedUnion(
    'cddPolicy',
    [
      z.discriminatedUnion(
        'screensCustomers',
        [
          institutionDetailsV2KycCddAnswersSchema.extend({
            cddPolicy: z.literal('NO'),
            screensCustomers: z.literal('NO'),
          }),
          institutionDetailsV2KycCddAnswersSchema.extend({
            cddPolicy: z.literal('NO'),
            screensCustomers: z.literal('YES'),
            screeningFrequency: institutionDetailsV2ScreeningFrequencySchema,
          }),
        ],
        {
          error: i18n.t('institutionDetails.kycCdd.errors.screensCustomers'),
        },
      ),
      z.discriminatedUnion(
        'screensCustomers',
        [
          institutionDetailsV2KycCddAnswersSchema.extend({
            cddPolicy: z.literal('YES'),
            cddPolicyDocument: institutionDetailsV2CddPolicyDocumentSchema,
            screensCustomers: z.literal('NO'),
          }),
          institutionDetailsV2KycCddAnswersSchema.extend({
            cddPolicy: z.literal('YES'),
            cddPolicyDocument: institutionDetailsV2CddPolicyDocumentSchema,
            screensCustomers: z.literal('YES'),
            screeningFrequency: institutionDetailsV2ScreeningFrequencySchema,
          }),
        ],
        {
          error: i18n.t('institutionDetails.kycCdd.errors.screensCustomers'),
        },
      ),
    ],
    {
      error: i18n.t('institutionDetails.kycCdd.errors.cddPolicy'),
    },
  )
  .superRefine((answers, context) => {
    if (answers.eddCategories.includes(EddCategory.OTHER) && !answers.eddCategoriesOther) {
      context.addIssue({ code: 'custom', path: ['eddCategoriesOther'], message: i18n.t('institutionDetails.kycCdd.errors.eddCategoriesOther') });
    }

    if (answers.prohibitedCategories.includes(ProhibitedCategory.OTHER) && !answers.prohibitedCategoriesOther) {
      context.addIssue({ code: 'custom', path: ['prohibitedCategoriesOther'], message: i18n.t('institutionDetails.kycCdd.errors.prohibitedCategoriesOther') });
    }
  });

export type InstitutionDetailsV2KycCdd = z.infer<typeof institutionDetailsV2KycCddSchema>;

export const institutionDetailsV2ExpectedActivityBaseSchema = z.object({
  netAnnualIncome: z.enum(ACTIVITY_BANDS, { error: i18n.t('institutionDetails.expectedActivity.errors.netAnnualIncome') }),
  expectedAnnualInvestment: z.enum(ACTIVITY_BANDS, { error: i18n.t('institutionDetails.expectedActivity.errors.expectedAnnualInvestment') }),
  monthlyFiatDeposits: z.enum(ACTIVITY_BANDS, { error: i18n.t('institutionDetails.expectedActivity.errors.monthlyFiatDeposits') }),
  monthlyFiatWithdrawals: z.enum(ACTIVITY_BANDS, { error: i18n.t('institutionDetails.expectedActivity.errors.monthlyFiatWithdrawals') }),
  monthlyCryptoDeposits: z.enum(ACTIVITY_BANDS, { error: i18n.t('institutionDetails.expectedActivity.errors.monthlyCryptoDeposits') }),
  monthlyCryptoWithdrawals: z.enum(ACTIVITY_BANDS, { error: i18n.t('institutionDetails.expectedActivity.errors.monthlyCryptoWithdrawals') }),
  monthlyTradingVolume: z.enum(TRADING_VOLUME_BANDS, { error: i18n.t('institutionDetails.expectedActivity.errors.monthlyTradingVolume') }),
  accountPurpose: z
    .array(z.enum(ACCOUNT_PURPOSES), { error: i18n.t('institutionDetails.expectedActivity.errors.accountPurpose') })
    .min(1, i18n.t('institutionDetails.expectedActivity.errors.accountPurpose')),
  accountPurposeOther: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('institutionDetails.expectedActivity.errors.accountPurposeOther') }).optional(),
  sourceOfFunds: z
    .array(z.enum(SOURCES_OF_FUNDS), { error: i18n.t('institutionDetails.expectedActivity.errors.sourceOfFunds') })
    .min(1, i18n.t('institutionDetails.expectedActivity.errors.sourceOfFunds')),
  sourceOfFundsOther: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('institutionDetails.expectedActivity.errors.sourceOfFundsOther') }).optional(),
  sourceOfFundsDocument: z.uuid(i18n.t('institutionDetails.expectedActivity.errors.sourceOfFundsDocument')),
});

export const institutionDetailsV2ExpectedActivitySchema = institutionDetailsV2ExpectedActivityBaseSchema.superRefine((answers, context) => {
  if (answers.accountPurpose.includes(AccountPurpose.OTHER) && !answers.accountPurposeOther) {
    context.addIssue({ code: 'custom', path: ['accountPurposeOther'], message: i18n.t('institutionDetails.expectedActivity.errors.accountPurposeOther') });
  }

  if (answers.sourceOfFunds.includes(SourceOfFunds.OTHER) && !answers.sourceOfFundsOther) {
    context.addIssue({ code: 'custom', path: ['sourceOfFundsOther'], message: i18n.t('institutionDetails.expectedActivity.errors.sourceOfFundsOther') });
  }
});

export type InstitutionDetailsV2ExpectedActivity = z.infer<typeof institutionDetailsV2ExpectedActivitySchema>;

const institutionDetailsV2EntityTaxResidenciesSchema = z
  .array(institutionDetailsV2EntityTaxResidencySchema, { error: i18n.t('institutionDetails.taxSelfCertification.errors.taxResidencies') })
  .min(1, i18n.t('institutionDetails.taxSelfCertification.errors.taxResidencies'))
  .max(5, i18n.t('institutionDetails.taxSelfCertification.errors.taxResidenciesTooMany'))
  .refine((residencies) => new Set(residencies.map(({ jurisdiction }) => jurisdiction)).size === residencies.length, {
    error: i18n.t('institutionDetails.taxSelfCertification.errors.taxResidenciesDuplicate'),
  });

export const institutionDetailsV2ControllingPersonSchema = institutionDetailsV2PersonSchema.extend({
  taxResidences: institutionDetailsV2TaxResidencesSchema,
  nationality: mainstreamCountrySchema(i18n.t('institutionDetails.person.errors.nationality')),
  // CARF allows several roles per person, e.g. an owner who is also the senior managing official
  controllingPersonTypes: z
    .array(z.enum(CONTROLLING_PERSON_TYPES), { error: i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersonTypes') })
    .min(1, i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersonTypes')),
  isPep: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.taxSelfCertification.errors.isPep') }),
});

export type InstitutionDetailsV2ControllingPerson = z.infer<typeof institutionDetailsV2ControllingPersonSchema>;

export const institutionDetailsV2ListedEntityNameSchema = freeTextSchema({
  maxLength: TEXT_MAX_LENGTH.MEDIUM,
  errors: textErrors('institutionDetails.taxSelfCertification.errors.listedEntityName'),
});

export const institutionDetailsV2ListedMarketSchema = freeTextSchema({
  maxLength: TEXT_MAX_LENGTH.MEDIUM,
  errors: textErrors('institutionDetails.taxSelfCertification.errors.listedMarket'),
});

export const institutionDetailsV2ActiveEntityCriterionSchema = z.enum(ACTIVE_ENTITY_CRITERIA, {
  error: i18n.t('institutionDetails.taxSelfCertification.errors.activeEntityCriterion'),
});

export const institutionDetailsV2TaxSelfCertificationSchema = z
  .object({
    declaration: z.object({
      accurate: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.declaration') }),
      consentToReporting: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.declaration') }),
      undertakeToNotifyChanges: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.declaration') }),
    }),
    signatory: z.object({
      partyId: z.uuid(),
      fullName: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('institutionDetails.taxSelfCertification.errors.signatoryFullName') }),
      capacity: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('institutionDetails.taxSelfCertification.errors.signatoryCapacity') }),
    }),
    date: z.iso.datetime(i18n.t('institutionDetails.taxSelfCertification.errors.date')),
  })
  .and(
    z.discriminatedUnion(
      'noTaxResidence',
      [
        z.object({
          noTaxResidence: z.literal('NO'),
          taxResidencies: institutionDetailsV2EntityTaxResidenciesSchema,
        }),
        z.object({
          noTaxResidence: z.literal('YES'),
          placeOfEffectiveManagement: mainstreamCountrySchema(i18n.t('institutionDetails.taxSelfCertification.errors.placeOfEffectiveManagement')),
        }),
      ],
      {
        error: i18n.t('institutionDetails.taxSelfCertification.errors.noTaxResidence'),
      },
    ),
  )
  .and(
    z.discriminatedUnion(
      'excludedPersonType',
      [
        z.object({
          excludedPersonType: z.enum([
            ExcludedPersonType.LISTED_CORPORATION,
            ExcludedPersonType.GOVERNMENTAL_ENTITY_INTERNATIONAL_ORGANISATION_OR_CENTRAL_BANK,
            ExcludedPersonType.FINANCIAL_INSTITUTION,
          ]),
        }),
        z.object({
          excludedPersonType: z.literal(ExcludedPersonType.RELATED_ENTITY_OF_LISTED_CORPORATION),
          listedEntityName: institutionDetailsV2ListedEntityNameSchema,
          listedMarket: institutionDetailsV2ListedMarketSchema,
        }),
        z.discriminatedUnion(
          'entityClassification',
          [
            z.object({
              excludedPersonType: z.literal(ExcludedPersonType.NONE),
              entityClassification: z.literal(EntityClassification.ACTIVE_ENTITY),
              activeEntityCriterion: institutionDetailsV2ActiveEntityCriterionSchema,
            }),
            z.object({
              excludedPersonType: z.literal(ExcludedPersonType.NONE),
              entityClassification: z.literal(EntityClassification.PASSIVE_ENTITY),
              controllingPersons: z
                .array(institutionDetailsV2ControllingPersonSchema, { error: i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersons') })
                .min(1, i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersons'))
                .max(20, i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersonsTooMany'))
                .refine(hasUniquePartyIds, { error: i18n.t('institutionDetails.taxSelfCertification.errors.duplicateParty') }),
            }),
          ],
          {
            error: i18n.t('institutionDetails.taxSelfCertification.errors.entityClassification'),
          },
        ),
      ],
      {
        error: i18n.t('institutionDetails.taxSelfCertification.errors.excludedPersonType'),
      },
    ),
  );

export type InstitutionDetailsV2TaxSelfCertification = z.infer<typeof institutionDetailsV2TaxSelfCertificationSchema>;

export const institutionDetailsV2ProductsSchema = z.object({
  futures: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.products.errors.futures') }),
});

export const institutionDetailsV2FuturesSchema = z.object({
  decisionMaker: institutionDetailsDecisionMakerSchema,
  execution: institutionDetailsExecutionSchema,
  clientCategorisation: institutionDetailsClientCategorisationSchema,
  reporting: institutionDetailsReportingSchema,
});

export type InstitutionDetailsV2FuturesKey = keyof z.infer<typeof institutionDetailsV2FuturesSchema>;

// Only asked of companies that handle customer funds
export const institutionDetailsV2CustomerFundsSchema = z.object({
  amlCtfSanctions: institutionDetailsV2AmlCtfSanctionsSchema,
  complianceTraining: institutionDetailsV2ComplianceTrainingSchema,
  kycCdd: institutionDetailsV2KycCddSchema,
});

export type InstitutionDetailsV2CustomerFundsKey = keyof z.infer<typeof institutionDetailsV2CustomerFundsSchema>;

const INSTITUTION_DETAILS_V2_CUSTOMER_FUNDS_REQUIRED_ERRORS: Record<InstitutionDetailsV2CustomerFundsKey, string> = {
  amlCtfSanctions: i18n.t('institutionDetails.amlCtfSanctions.errors.requiredForCustomerFunds'),
  complianceTraining: i18n.t('institutionDetails.complianceTraining.errors.requiredForCustomerFunds'),
  kycCdd: i18n.t('institutionDetails.kycCdd.errors.requiredForCustomerFunds'),
};

export const institutionDetailsV2OnboardingSchema = z.object({
  incorporation: institutionDetailsIncorporationSchema,
  branchOperations: institutionDetailsBranchOperationsSchema,
  company: institutionDetailsV2CompanySchema,
  businessProfile: institutionDetailsV2BusinessProfileSchema,
  ownership: institutionDetailsV2OwnershipSchema,
  regulatoryInformation: institutionDetailsV2RegulatoryInformationSchema,
  // Required by the complete schema unless the company trades only its own funds
  amlCtfSanctions: institutionDetailsV2CustomerFundsSchema.shape.amlCtfSanctions.optional(),
  complianceTraining: institutionDetailsV2CustomerFundsSchema.shape.complianceTraining.optional(),
  kycCdd: institutionDetailsV2CustomerFundsSchema.shape.kycCdd.optional(),
  expectedActivity: institutionDetailsV2ExpectedActivitySchema,
  taxSelfCertification: institutionDetailsV2TaxSelfCertificationSchema,
  products: institutionDetailsV2ProductsSchema,
  // Required by the complete schema when futures is selected
  decisionMaker: institutionDetailsV2FuturesSchema.shape.decisionMaker.optional(),
  execution: institutionDetailsV2FuturesSchema.shape.execution.optional(),
  clientCategorisation: institutionDetailsV2FuturesSchema.shape.clientCategorisation.optional(),
  reporting: institutionDetailsV2FuturesSchema.shape.reporting.optional(),
  authorisation: institutionDetailsAuthorisationSchema,
});

export type InstitutionDetailsV2OnboardingKey = keyof z.infer<typeof institutionDetailsV2OnboardingSchema>;

export const institutionDetailsV2OnboardingCompleteSchema = institutionDetailsV2OnboardingSchema.superRefine((answers, context) => {
  if (answers.products.futures === 'YES') {
    for (const section of Object.keys(institutionDetailsV2FuturesSchema.shape) as InstitutionDetailsV2FuturesKey[]) {
      if (!answers[section]) {
        context.addIssue({ code: 'custom', path: [section], message: i18n.t('institutionDetails.products.errors.futuresAnswersRequired') });
      }
    }

    // Futures transactions are reported against the company's LEI
    if (!answers.company.leiNumber) {
      context.addIssue({ code: 'custom', path: ['company', 'leiNumber'], message: i18n.t('institutionDetails.products.errors.leiNumber') });
    }
  }

  if (answers.businessProfile.fundsOwnership === FundsOwnership.OWN) return;

  for (const section of Object.keys(institutionDetailsV2CustomerFundsSchema.shape) as InstitutionDetailsV2CustomerFundsKey[]) {
    if (!answers[section]) {
      context.addIssue({ code: 'custom', path: [section], message: INSTITUTION_DETAILS_V2_CUSTOMER_FUNDS_REQUIRED_ERRORS[section] });
    }
  }
});

export type InstitutionDetailsV2 = z.infer<typeof institutionDetailsV2OnboardingCompleteSchema>;

export const institutionDetailsV2FuturesCompleteSchema = institutionDetailsV2FuturesSchema.extend({
  products: z.object({
    futures: z.literal('YES'),
  }),
});

export const institutionDetailsV2PartialSchema = institutionDetailsV2OnboardingSchema.partial();

export type InstitutionDetailsV2Partial = z.infer<typeof institutionDetailsV2PartialSchema>;
